import { HTMLAttributes } from 'react';
import styled from 'styled-components';

import { ConditionalWrapper } from 'shared/components/ConditionalWrapper';
import { Image } from 'shared/components/Image';
import { IProductSearch } from 'shared/types/ProductSearch';
import { localizeCondition } from 'shared/utils/localizeCondition';
import { LocalizedCurrency } from 'shared/components/LocalizedCurrency';
import { Stack } from 'shared/components/Stack';
import { startCase } from 'shared/utils/startCase';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { variableFontRegular } from 'styles/theme/text';

export interface IUpdate2022VariantGridItemProps extends HTMLAttributes<HTMLDivElement> {
  'data-qa'?: string;
  imageSize?: { width?: number; height?: number };
  variant: IProductSearch;
}

export const Update2022VariantGridItem = ({
  variant,
  imageSize,
  ...rest
}: IUpdate2022VariantGridItemProps) => {
  const { name, productType } = useStoreSelector((state) => state.pdp.productTemplate);
  const {
    id,
    localizedPriceCents,
    thumbOuterPictureUrl,
    outerPictureUrl,
    boxCondition,
    shoeCondition,
  } = variant;

  const imageSrc = thumbOuterPictureUrl || outerPictureUrl;
  const imageAlt = `${name} - ${id}`;
  const price = localizedPriceCents?.amount;
  // NOTE: Looks strange but intentional. Non-sneakers don't have boxes so we display `shoeCondition` in those cases
  const condition = productType === 'sneakers' ? boxCondition : shoeCondition;

  return (
    <Stack {...rest} spacing="8px" direction="column">
      <ConditionalWrapper
        if={!!imageSize}
        with={(children) => (
          <div style={{ width: imageSize?.width, height: imageSize?.height }}>{children}</div>
        )}
      >
        <Image
          data-qa="variant_grid_item_image"
          sizes="200px"
          src={imageSrc}
          alt={imageAlt}
          disableLazyLoad
        />
      </ConditionalWrapper>
      <Stack spacing="4px" direction="column">
        <Update2022VariantGridItem.Condition data-qa="variant_grid_item_condition">
          {startCase(localizeCondition(condition))}
        </Update2022VariantGridItem.Condition>
        {Boolean(price) && (
          <Update2022VariantGridItem.Price data-qa="variant_grid_item_price" price={price} />
        )}
      </Stack>
    </Stack>
  );
};

Update2022VariantGridItem.Condition = styled.span`
  ${variableFontRegular({ size: '9px', height: '12px' })}
`;

Update2022VariantGridItem.Price = styled(LocalizedCurrency)`
  ${variableFontRegular({ size: '11px', height: '14px' })}
`;
